import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from './store/hooks'
import { restoreSession } from './store/authSlice'

import ProtectedRoute from './components/layout/ProtectedRoute'
import AppShell from './components/layout/AppShell'
import LoginScreen from './components/auth/LoginScreen'
import ResetPasswordScreen from './components/auth/ResetPasswordScreen'
import SetNewPasswordScreen from './components/auth/SetNewPasswordScreen'
import ConsentScreen from './components/auth/ConsentScreen'
import ProfileSetupScreen from './components/auth/ProfileSetupScreen'
import EditProfileScreen from './components/auth/EditProfileScreen'
import Dashboard from './components/dashboard/Dashboard'
import CategoryPage from './components/category/CategoryPage'
import ToolPage from './components/tool/ToolPage'
import AdminPanel from './components/admin/AdminPanel'
import ManageTeam from './components/manage/ManageTeam'

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const profile = useAppSelector((s) => s.auth.profile)
  const [step, setStep] = useState<'consent' | 'profile' | 'ready'>('ready')

  useEffect(() => {
    if (!profile) return
    if (!profile.consented) {
      setStep('consent')
    } else if (!profile.display_name) {
      setStep('profile')
    } else {
      setStep('ready')
    }
  }, [profile])

  if (step === 'consent') {
    return <ConsentScreen onComplete={() => setStep('profile')} />
  }
  if (step === 'profile') {
    return <ProfileSetupScreen onComplete={() => setStep('ready')} />
  }
  return <>{children}</>
}

export default function App() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(restoreSession())
  }, [dispatch])

  return (
    <BrowserRouter>
      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/set-password" element={<SetNewPasswordScreen />} />

        {/* Protected routes with onboarding gate */}
        <Route element={<ProtectedRoute />}>
          <Route
            element={
              <OnboardingGate>
                <AppShell />
              </OnboardingGate>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/edit-profile" element={<EditProfileScreen />} />
            <Route path="/category/:sectionKey/:categoryKey" element={<CategoryPage />} />
            <Route path="/tool/:sectionKey/:categoryKey/:toolKey" element={<ToolPage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/manage" element={<ManageTeam />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
